import React, { useState, useEffect, useMemo } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import { close } from 'ionicons/icons';
import { useCreateNeed, useUpdateNeed } from '@/hooks/useNeeds';
import { useFamilies } from '@/hooks/useFamilies';
import type { Need, NeedCategory, NeedPriority, NeedStatus, NeedFormData } from '@/types/need';
import { NEED_CATEGORY_LABELS, NEED_PRIORITY_LABELS, NEED_STATUS_LABELS } from '@/utils/labels';
import SearchableSelect from '@/components/shared/SearchableSelect';

interface NeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  need?: Need | null;
  familyId?: string;
  memberId?: string;
}

const EMPTY_FORM: NeedFormData = {
  family_id: null,
  member_id: null,
  category: 'altele',
  title: '',
  description: null,
  priority: 'normala',
  due_date: null,
};

const NeedModal: React.FC<NeedModalProps> = ({ isOpen, onClose, onSuccess, need, familyId, memberId }) => {
  const [presentToast] = useIonToast();
  const createMut = useCreateNeed();
  const updateMut = useUpdateNeed();
  const { data: familiesData } = useFamilies({ page_size: 500 });
  const familiesList = familiesData?.data ?? [];
  const familyOptions = useMemo(() =>
    familiesList.map((f) => ({ value: f.id, label: f.display_name ?? f.name })),
    [familiesList],
  );

  const isEdit = !!need;

  const [formData, setFormData] = useState<NeedFormData & { status?: NeedStatus }>(EMPTY_FORM);

  useEffect(() => {
    if (isOpen) {
      if (need) {
        setFormData({
          family_id: need.family_id,
          member_id: need.member_id,
          category: need.category,
          title: need.title,
          description: need.description,
          priority: need.priority,
          due_date: need.due_date?.slice(0, 10) ?? null,
          status: need.status,
        });
      } else {
        setFormData({
          ...EMPTY_FORM,
          family_id: familyId ?? null,
          member_id: memberId ?? null,
        });
      }
    }
  }, [isOpen, need, familyId, memberId]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      void presentToast({ message: 'Titlul este obligatoriu.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }

    try {
      if (isEdit && need) {
        await updateMut.mutateAsync({ id: need.id, data: formData });
        void presentToast({ message: 'Nevoia actualizată.', duration: 2000, color: 'success', position: 'bottom' });
      } else {
        await createMut.mutateAsync(formData);
        void presentToast({ message: 'Nevoia înregistrată.', duration: 2000, color: 'success', position: 'bottom' });
      }
      onSuccess?.();
      onClose();
    } catch {
      void presentToast({ message: 'Eroare la salvare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  if (!isOpen) return null;

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="logout-overlay" onClick={onClose}>
      <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? 'Editează Nevoia' : 'Nevoie Nouă'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
          </button>
        </div>

        {/* Title */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Titlu *</label>
          <input
            className="form-input"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Descriere scurtă a nevoii..."
          />
        </div>

        {/* Category + Priority row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div className="form-field">
            <label className="form-field__label">Categorie *</label>
            <select
              className="form-input"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as NeedCategory })}
            >
              {(Object.keys(NEED_CATEGORY_LABELS) as NeedCategory[]).map((cat) => (
                <option key={cat} value={cat}>{NEED_CATEGORY_LABELS[cat]}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Prioritate</label>
            <select
              className="form-input"
              value={formData.priority ?? 'normala'}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as NeedPriority })}
            >
              {(Object.keys(NEED_PRIORITY_LABELS) as NeedPriority[]).map((p) => (
                <option key={p} value={p}>{NEED_PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status (edit only) */}
        {isEdit && (
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-field__label">Status</label>
            <select
              className="form-input"
              value={formData.status ?? 'deschisa'}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as NeedStatus })}
            >
              {(Object.keys(NEED_STATUS_LABELS) as NeedStatus[]).map((s) => (
                <option key={s} value={s}>{NEED_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
        )}

        {/* Family (if not locked) */}
        {!familyId && !memberId && (
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-field__label">Familie</label>
            <SearchableSelect
              options={familyOptions}
              value={formData.family_id ?? null}
              onChange={(val) => setFormData({ ...formData, family_id: val })}
              placeholder="Caută familie..."
              emptyLabel="Fără familie"
            />
          </div>
        )}

        {/* Due date */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Termen</label>
          <input
            className="form-input"
            type="date"
            value={formData.due_date ?? ''}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value || null })}
          />
        </div>

        {/* Description */}
        <div className="form-field" style={{ marginBottom: 24 }}>
          <label className="form-field__label">Descriere</label>
          <textarea
            className="form-input"
            value={formData.description ?? ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
            placeholder="Detalii suplimentare..."
            rows={3}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="form-btn form-btn--secondary" onClick={onClose}>
            Anulează
          </button>
          <button className="form-btn form-btn--primary" onClick={handleSubmit} disabled={isPending}>
            {isPending && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
            {isEdit ? 'Salvează' : 'Înregistrează'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NeedModal;
